import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'components/Form/Select';
import Input from 'components/Form/Input';
import { currencyMask, floatUnmask } from 'utils/masks';
import { formatCurrency } from 'utils/formatCurrency';

const LowerLtvPaymentForm = ({
  payFromAccounts,
  payFromAccount,
  paymentAmountOptions,
  paymentAmountType,
  customPaymentAmount,
  paymentDate,
  payFromAccountLabel,
  paymentAmountLabel,
  isGoalLinkedPaymentAmount,
  onPayFromAccountChange,
  onPaymentAmountTypeChange,
  onCustomPaymentAmountChange,
  initialEditing,
  pastDueAmount,
  allowAmountAndDateEdit,
  renderPaymentDate,
}) => {
  const [isEditing, setIsEditing] = useState(initialEditing || false);

  const handleCustomAmountBlur = (event) => {
    if (onCustomPaymentAmountChange) {
      onCustomPaymentAmountChange({
        target: {
          name: event.target.name,
          value: formatCurrency(floatUnmask(event.target.value || '0'), 2),
        },
      });
    }
  };

  if (isEditing) {
    return (
      <div className="lower-ltv-modal__payment-edit">
        <Select
          className="mb-2"
          name="paymentAccountId"
          data={payFromAccounts}
          value={payFromAccount}
          onChange={onPayFromAccountChange}
          label="Pay From Account"
          isRequired
        />
        {typeof renderPaymentDate === 'function' ? (
          renderPaymentDate({ isDisabled: !allowAmountAndDateEdit })
        ) : (
          <Input
            className="mb-2"
            name="paymentDate"
            label="Payment Date"
            value={paymentDate}
            isDisabled
          />
        )}
        <Select
          className="mb-2"
          name="selectedAmountOption"
          label="Payment Amount"
          data={paymentAmountOptions}
          value={paymentAmountType}
          onChange={onPaymentAmountTypeChange}
          isRequired
          isDisabled={!allowAmountAndDateEdit}
        />
        {paymentAmountType === '2' && (
          <Input
            className="lower-ltv-modal__amount-input"
            label="Custom Payment Amount"
            name="customPaymentAmount"
            inputMode="decimal"
            value={customPaymentAmount}
            onChange={onCustomPaymentAmountChange}
            onBlur={handleCustomAmountBlur}
            isMasked={currencyMask}
            isRequired
            isDisabled={!allowAmountAndDateEdit}
          />
        )}
        <div className="lower-ltv-modal__edit-wrap" />
        {pastDueAmount && (
          <div className="lower-ltv-modal__payment-banner">
            {`This single payment covers your $${pastDueAmount} past due and reduces your LTV.`}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="lower-ltv-modal__payment-summary">
      <dl className="lower-ltv-modal__payment-details">
        <div className="lower-ltv-modal__payment-detail">
          <dt>Pay From Account</dt>
          <dd>{payFromAccountLabel || '—'}</dd>
        </div>
        <div className="lower-ltv-modal__payment-detail">
          <dt>Payment Date</dt>
          <dd>{paymentDate}</dd>
        </div>
        <div className="lower-ltv-modal__payment-detail">
          <dt>Amount</dt>
          <dd className={isGoalLinkedPaymentAmount ? 'lower-ltv-modal__payment-amount--suggested' : undefined}>
            {paymentAmountLabel}
          </dd>
        </div>
      </dl>
      <div className="lower-ltv-modal__edit-wrap">
        <span
          className="lower-ltv-modal__edit-link"
          role="button"
          tabIndex={0}
          onClick={() => setIsEditing(true)}
          onKeyDown={(e) => e.key === 'Enter' && setIsEditing(true)}
        >
          EDIT
        </span>
      </div>
      {pastDueAmount && (
        <div className="lower-ltv-modal__payment-banner">
          {`This single payment covers your $${pastDueAmount} past due and reduces your LTV.`}
        </div>
      )}
    </div>
  );
};

LowerLtvPaymentForm.propTypes = {
  payFromAccounts: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    title: PropTypes.string,
  })),
  payFromAccount: PropTypes.string,
  paymentAmountOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    title: PropTypes.string,
  })),
  paymentAmountType: PropTypes.string,
  customPaymentAmount: PropTypes.string,
  paymentDate: PropTypes.string.isRequired,
  payFromAccountLabel: PropTypes.string,
  paymentAmountLabel: PropTypes.string,
  isGoalLinkedPaymentAmount: PropTypes.bool,
  onPayFromAccountChange: PropTypes.func,
  onPaymentAmountTypeChange: PropTypes.func,
  onCustomPaymentAmountChange: PropTypes.func,
  initialEditing: PropTypes.bool,
  pastDueAmount: PropTypes.string,
  allowAmountAndDateEdit: PropTypes.bool,
  renderPaymentDate: PropTypes.func,
};

LowerLtvPaymentForm.defaultProps = {
  payFromAccounts: [],
  payFromAccount: '',
  paymentAmountOptions: [],
  paymentAmountType: '0',
  customPaymentAmount: '',
  payFromAccountLabel: '',
  paymentAmountLabel: '',
  isGoalLinkedPaymentAmount: false,
  onPayFromAccountChange: null,
  onPaymentAmountTypeChange: null,
  onCustomPaymentAmountChange: null,
  initialEditing: false,
  pastDueAmount: '',
  allowAmountAndDateEdit: false,
  renderPaymentDate: null,
};

export default LowerLtvPaymentForm;
